import {Component, OnInit, ViewChild, OnDestroy, ChangeDetectorRef, inject} from '@angular/core';
import {
  NgxDaterangepickerBootstrapComponent,
  NgxDaterangepickerBootstrapDirective
} from "ngx-daterangepicker-bootstrap";
import {FormsModule} from "@angular/forms";
import dayjs from "dayjs";
import {GetClaimStatsService} from "./services/get-claim-stats.service";
import {DownloadCsvService} from "./services/download-csv-service.service";
import {ClaimStatsDto} from "../auto-generated-server-dtos/adjuster/claim-stats/claim-stats-dto";
import {CurrencyPipe, DecimalPipe, NgForOf, NgIf} from "@angular/common";
import Chart from 'chart.js/auto';
import {of, Subject} from "rxjs";
import {map} from "rxjs/operators";
import {DataTablesModule} from "angular-datatables";
import { HttpParams } from '@angular/common/http';
import { FormatAdjusterValuePipe } from './pipes/format-adjuster-value-pipe.pipe.pipe';


@Component({
  selector: 'app-stats',
  standalone: true,
  imports: [FormsModule, NgxDaterangepickerBootstrapDirective, CurrencyPipe, DecimalPipe, FormatAdjusterValuePipe, NgIf, DataTablesModule],
  templateUrl: './stats.component.html',
  styleUrl: './stats.component.scss',
  providers: [DecimalPipe, CurrencyPipe],
})
export class StatsComponent implements OnInit, OnDestroy {
  protected readonly Math = Math;
  protected readonly Object = Object;

  ClaimStats!: ClaimStatsDto;
  claimTypeFilter: number = 0;
  public statusWithinDateRangeTotal: number = 0;
  public statusOutsideDateRangeTotal: number = 0;

  @ViewChild(NgxDaterangepickerBootstrapDirective, { static: false }) datepickerDirective!: NgxDaterangepickerBootstrapDirective;
  public dateRangePicker: any;
  public chartWithinDateRange: any;
  public chartOutsideDateRange: any;

  public dtAdjusterOptions: DataTables.Settings = {};
  public dtRepairCenterOptions: DataTables.Settings = {};
  public dtAdjusterTrigger: Subject<any> = new Subject<any>();
  public dtRepairCenterTrigger: Subject<any> = new Subject<any>();
  public adjusterData: any[] = [];
  public repairCenterData: any[] = [];
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  public readonly minDate = dayjs('2025-05-14')
  private formatAdjusterValuePipe = new FormatAdjusterValuePipe();

  private currYear = new Date().getFullYear();
  ranges: any = {
    'This month': [dayjs().startOf('month'), dayjs().endOf('month')],
    'Last month': [
      dayjs().startOf('month').subtract(1, 'month'),
      dayjs().endOf('month').subtract(1, 'month'),
    ],
    'This Fiscal': [dayjs(`${this.currYear}-01-01`), dayjs(`${this.currYear}-12-31`)],
    'Last Fiscal': [dayjs(`${this.currYear - 1}-01-01`), dayjs(`${this.currYear - 1}-12-31`)],
    'All Time': [dayjs('2023-01-01'), dayjs()]
  };
  locale: any = {
    format: 'YYYY-MM-DD',
    displayFormat: 'MMMM D, YYYY'
  };
  unresolvedNavigationLink: string = `adjuster/claim-list?${new HttpParams({fromString: `unresolved=true`})}`
  
  constructor(
    private getClaimStatsService: GetClaimStatsService,
    private downloadCsvService: DownloadCsvService,
    private decimalPipe: DecimalPipe,
    private currencyPipe: CurrencyPipe
  ) {
    this.dateRangePicker = {
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('month')
    };
  }

  ngOnInit(): void {
    this.getStats();

    this.dtAdjusterOptions = {
      dom: '<"row"<"col-md-6"i><"col-md-6"f>><"row"<"px-0"t>><"row"<"col-6 mt-2"l><"col-6"p>>',
      pageLength: -1,
      paging: false,
      info: false,
      searching: false,
      stateSave: true,
      data: this.adjusterData,
      destroy: true,
      columns: [
        { 
          title: 'Adjuster',
          data: 'adjusterName',
          className: 'w-25',
          render: (data, type, row) => { 
            return `${row.firstName} ${row.lastName}`; 
          } 
        },
        {
          title: 'Total Accepted Time',
          data: 'acceptedMinutes',
          className: 'text-end pe-sort',
          render: (data) => {
            const valueInHours = data / 60;
            return this.formatAdjusterValuePipe.transform(valueInHours, '1.1-1', true);
          }
        },
        { 
          title: 'Average Accepted Time', 
          data: 'acceptedMinutesAverage',
          className: 'text-end pe-sort',
          render: (data) => {
            const valueInHours = data / 60;
            return this.formatAdjusterValuePipe.transform(valueInHours, '1.1-1', true);
          }          
        },
        { 
          title: 'Total Rejected Time', 
          data: 'rejectedMinutes',
          className: 'text-end pe-sort',
          render: (data) => {
            const valueInHours = data / 60;
            return this.formatAdjusterValuePipe.transform(valueInHours, '1.1-1', true);
          }          
        },
        { 
          title: 'Average Rejected Time', 
          data: 'rejectedMinutesAverage',
          className: 'text-end pe-sort',
          render: (data) => {
            const valueInHours = data / 60;
            return this.formatAdjusterValuePipe.transform(valueInHours, '1.1-1', true);
          }          
        },
        { title: 'Accepted', data: 'acceptedCount', className: 'text-end pe-sort' },
        { title: 'Rejected', data: 'rejectedCount', className: 'text-end pe-sort' },
        { title: 'Unresolved', data: 'unresolvedCount', className: 'text-end pe-sort' },
        { title: 'Total', data: 'assignedCount', className: 'text-end pe-sort' },
      ]
    }

    this.dtRepairCenterOptions = {
      dom: '<"row"<"col-md-6"i><"col-md-6"f>><"row"<"px-0"t>><"row"<"col-6 mt-2"l><"col-6"p>>',
      lengthMenu: [[25, 50, 100, -1], [25, 50, 100, "All"]],
      pageLength: 50,
      paging: true,
      info: true,
      stateSave: true,
      language: {
        info: 'Showing _START_ to _END_ of _TOTAL_ repair centers',
        infoEmpty: 'Showing 0 repair centers',
        infoFiltered: '<br>Filtered from _MAX_ total repair centers',
        zeroRecords: 'No matching repair centers found',
        lengthMenu: 'Show _MENU_ repair centers'
      },
      data: this.repairCenterData,
      destroy: true,
      columns: [
        { title: 'Name', data: 'name', className: 'w-35' },
        { title: 'City', data: 'city', className: 'w-25' },
        { title: 'Province / State', data: 'province', className: 'w-15' },
        {
          title: 'Number of Claims',
          data: 'total',
          className: 'text-end pe-sort w-10',
          render: (data) => {
            return this.decimalPipe.transform(data, '1.0-0') || '0';
          }
        },
        {
          title: 'Retail Amount',
          data: 'retailTotal',
          className: 'text-end pe-sort fw-bolder w-10',
          render: (data) => {
            return this.currencyPipe.transform(data, '', '', '1.0-0') || '$0';
          }
        },
      ]
    }
  }

  refreshAdjusterTable(): void {
    this.adjusterData = this.ClaimStats?.adjusters?.adjusters || [];
    this.dtAdjusterOptions.data = this.adjusterData;
    this.dtAdjusterTrigger.next(null);
  }

  refreshRepairCenterTable(): void {
    this.repairCenterData = this.ClaimStats?.repairCentres?.repairCentres || [];
    this.dtRepairCenterOptions.data = this.repairCenterData;
    this.dtRepairCenterTrigger.next(null);
  }

  createSubmittedWithinDateRangeChart(){
    of(this.ClaimStats).pipe(
       map(claimStats => {
         return claimStats;
       })
    )

    if (this.chartWithinDateRange) this.chartWithinDateRange.destroy();

    this.chartWithinDateRange = new Chart("SubmittedWithinDateRangeChart", {
      type: 'bar', //this denotes tha type of chart

      data: {// values on X-Axis
        labels: ['Submitted', 'Viewed', 'Accepted', 'Rejected' ],
        datasets: [
          {
            data: [this.ClaimStats.statusWithinDateRange.submitted, this.ClaimStats.statusWithinDateRange.viewed, this.ClaimStats.statusWithinDateRange.approved, this.ClaimStats.statusWithinDateRange.rejected],
            label: '',
            backgroundColor: [
              getComputedStyle(document.documentElement).getPropertyValue('--bs-info'),
              getComputedStyle(document.documentElement).getPropertyValue('--bs-danger'),
              getComputedStyle(document.documentElement).getPropertyValue('--bs-success'),
              getComputedStyle(document.documentElement).getPropertyValue('--bs-warning')
            ],
            borderRadius: 5
          },
        ]
      },
      options: {
        aspectRatio: 2.5,
        plugins: {
          legend: { display: false }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {  // eslint-disable-line no-unused-vars
                value = parseFloat(value.toString());
                return (Math.floor(value) === value ? value : '');
              }
            }
          },
          x: {
            grid: {
              display: false
            }
          }
        }
      }
    });
  }

  createSubmittedOutsideDateRangeChart(){

    of(this.ClaimStats).pipe(
       map(claimStats => {
         return claimStats;
       })
    )

    if (this.chartOutsideDateRange) this.chartOutsideDateRange.destroy();

    this.chartOutsideDateRange = new Chart("SubmittedOutsideDateRangeChart", {
      type: 'bar', //this denotes tha type of chart

      data: {// values on X-Axis
        labels: ['Submitted', 'Viewed', 'Accepted', 'Rejected' ],
        datasets: [
          {
            data: [
              this.ClaimStats.statusOutsideDateRange.submitted,
              this.ClaimStats.statusOutsideDateRange.viewed,
              this.ClaimStats.statusOutsideDateRange.approved,
              this.ClaimStats.statusOutsideDateRange.rejected
            ],
            label: '',
            backgroundColor: [
              getComputedStyle(document.documentElement).getPropertyValue('--bs-info'),
              getComputedStyle(document.documentElement).getPropertyValue('--bs-danger'),
              getComputedStyle(document.documentElement).getPropertyValue('--bs-success'),
              getComputedStyle(document.documentElement).getPropertyValue('--bs-warning')
            ],
            borderRadius: 5
          },
        ]
      },
      options: {
        aspectRatio: 2.5,
        plugins: {
          legend: { display: false }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {  // eslint-disable-line no-unused-vars
                value = parseFloat(value.toString());
                return (Math.floor(value) === value ? value : '');
              }
            }
          },
          x: {
            grid: {
              display: false
            }
          }
        }
      }
    });
  }

  getStats(): void {
    this.getClaimStatsService.getClaimStats(this.dateRangePicker.startDate, this.dateRangePicker.endDate, this.claimTypeFilter).subscribe((claimStats: ClaimStatsDto) => {
      this.ClaimStats = claimStats;
      this.cdr.detectChanges();
      this.createSubmittedWithinDateRangeChart();
      this.createSubmittedOutsideDateRangeChart();
      this.statusWithinDateRangeTotal = Object.values(this.ClaimStats.statusWithinDateRange).reduce((acc: number, val: number) => acc + val, 0);
      this.statusOutsideDateRangeTotal = Object.values(this.ClaimStats.statusOutsideDateRange).reduce((acc: number, val: number) => acc + val, 0);
      this.refreshAdjusterTable();
      this.refreshRepairCenterTable();
    });
  }

  datesUpdatedRange($event: any) {
    if ($event.startDate == null && $event.endDate == null) {
      return
    }
    this.getStats();
  }

  openDateRangePicker() {
    this.datepickerDirective.open();
  }

  downloadAdjusterStats(): void {
    const filename = `Adjuster Claims Stats - ${this.dateRangePicker.startDate.format('YYYY-MM-DD')} to ${this.dateRangePicker.endDate.format('YYYY-MM-DD')}`;
    this.downloadCsvService.downloadAdjusterData(this.ClaimStats.adjusters, filename);
  }

  downloadRepairCenterStats(): void {
    const filename = `Repair Center Claims Stats - ${this.dateRangePicker.startDate.format('YYYY-MM-DD')} to ${this.dateRangePicker.endDate.format('YYYY-MM-DD')}`;
    this.downloadCsvService.downloadRepairCenterData(this.ClaimStats.repairCentres, filename);
  }

  ngOnDestroy(): void {
    this.dtAdjusterTrigger.unsubscribe();
    this.dtRepairCenterTrigger.unsubscribe();
  }

}
